<script>
/* Imports */
import { mapActions, mapGetters } from 'vuex';

/* Analytics */
import {
  sendAnalyticsEvent,
  sanitizeAnalyticsString,
  getAnalyticsPriceData,
} from '../helpers/ga/analytics.js';

/* Composables */
import { useGenericAnalyticsDataHelper } from '../composables/useAnalyticsData.js';
import { useScreenResize } from '../composables/useScreenResize.js';

/* Helpers */
import {
  breakpoints,
} from '../helpers/mainHelpers.js';

/* Components */
import ProductWarning from './ProductWarning.vue';
import ATCButton from './ATCButton.vue';
import FakeATCButton from './FakeATCButton.vue';

export default {
  name: 'CtasWrapper',
  components: {
    ProductWarning,
    ATCButton,
    FakeATCButton,
  },
  props: {
    showAddToCartBtn: {
      type: Boolean,
      default: false,
    },
    addToCartHref: {
      type: String,
      default: '',
    },
    addToCartText: {
      type: String,
      default: '',
    },
    showDealerBtn: {
      type: Boolean,
      default: false,
    },
    dealerHref: {
      type: String,
      default: '',
    },
    dealerText: {
      type: String,
      default: '',
    },
    expressWarning: {
      type: String,
      default: '',
    },
    showExpressWarning: {
      type: Boolean,
      default: true,
    },
    currencyCode: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    /* Initialize Composables */
    const { genericAnalyticsData } = useGenericAnalyticsDataHelper();
    const { screenWidth } = useScreenResize();
    return { genericAnalyticsData, screenWidth };
  },
  data() {
    return {
      isOutOfView: false,
      isPinnedToFooter: false,
    };
  },
  computed: {
    ...mapGetters({
      productName: 'getProductName',
      globalProductName: 'getGlobalProductName',
      productId: 'getProductId',
      priceObj: 'getPriceData',
      seoAttributes: 'getSeoAttributes',
      productVariation: 'getProductVariation',
      partNumber: 'getPartNumber',
      customizeButtonText: 'getCustomizeButtonText',
      showStickyAddToCartBtn: 'getShowStickyAddToCartBtn',
      productDisplayCustomizeButton: 'getProductDisplayCustomizeButton',
      productCustomizeButtonUrl: 'getProductCustomizeButtonUrl',
    }),
    isMobileView() {
      return this.screenWidth <= breakpoints['mobile-m'];
    },
    enableStickyAddToCartBtn() {
      return this.showStickyAddToCartBtn && this.isMobileView;
    },
    showAPACWidget() {
      return window?.showAPACWidget;
    },
  },
  watch: {
    isOutOfView(newState) {
      this.setShowStickyAddToCart(newState);
    },
    isPinnedToFooter(newState) {
      this.setPinStickyATCButton(newState);
    },
  },
  mounted() {
    if (this.isMobileView) {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('click', () => setTimeout(this.handleFooter, 50));
    }
    this.handleScroll();
  },
  beforeUnmount() {
    if (this.isMobileView) {
      window.removeEventListener('scroll', this.handleScroll);
      window.addEventListener('click', () => setTimeout(this.handleFooter, 50));
    }
  },
  methods: {
    ...mapActions([
      'setShowStickyAddToCart',
      'setPinStickyATCButton',
      'setFooterVisibleHeight',
    ]),
    handleFooter() {
      const footer = document.getElementsByClassName('gf__footer')[0];
      if (!footer) return;
      const footerRect = footer.getBoundingClientRect();

      this.isPinnedToFooter = footerRect.top < window.innerHeight;

      if (this.isPinnedToFooter) {
        const offsetFromBottom = window.innerHeight - footerRect.top;
        this.setFooterVisibleHeight(offsetFromBottom);
      }
    },
    handleScroll() {
      const addToCartBtn = document.getElementById('js__cta__buy') || document.getElementById('js__cta__bundle');
      if (!addToCartBtn) return;

      const atcRect = addToCartBtn.getBoundingClientRect();
      this.isOutOfView = atcRect.bottom < 0;
      this.handleFooter();
    },
    findADealerBtnClick() {
      sendAnalyticsEvent({
        // Send these as undefined since we're going to work in another context to update these values
        ...this.genericAnalyticsData(
          undefined,
          undefined,
        ),
        tealium_event: 'find_dealer',
        event_label: 'Find Dealer',
        link_type: 'button',
        product_id: this.productId,
        product_name: sanitizeAnalyticsString(this.globalProductName),
        product_variant: sanitizeAnalyticsString(this.productVariation),
        event_category: `Product Page >> ${sanitizeAnalyticsString(this.globalProductName)}`,
        event_action: 'click',
        product_price: getAnalyticsPriceData(this).listPrice, // price without discount
        product_sku: this.partNumber,
        discount: this.priceObj?.savings?.price ?? 'NA',
        promotion: 'no',
        product_family: this.seoAttributes?.attributeGroupName ?? 'NA',
      });
    },
  },
};
</script>

<template>
  <div
    id="js__product__ctas"
    class="app__product__cta"
  >
    <ProductWarning
      v-if="showExpressWarning"
      id="js__cta__warning"
      class="app__product__cta__warning"
      :content="expressWarning"
    />
    <div v-if="!showAPACWidget">
      <ATCButton
        id="js__cta__buy"
        :product-id="productId"
        :part-number="partNumber"
        :product-variation="productVariation"
        :show-sticky-add-to-cart-btn="enableStickyAddToCartBtn"
        :add-to-cart-href="addToCartHref"
        :add-to-cart-text="addToCartText"
      />
      <g-button
        v-if="productDisplayCustomizeButton"
        id="js__cta__customize"
        :href="productCustomizeButtonUrl"
        class="app__product__cta__button app__product__cta__button__customize"
        size="large"
        theme="light"
      >
        {{ customizeButtonText }}
      </g-button>
      <g-button
        v-if="showDealerBtn"
        id="js__cta__dealer"
        :href="dealerHref"
        class="app__product__cta__button app__product__cta__button__dealer"
        size="large"
        theme="light"
        @click="findADealerBtnClick"
      >
        {{ dealerText }}
      </g-button>
      <FakeATCButton
        :product-id="productId"
        :part-number="partNumber"
        :product-variation="productVariation"
        :show-sticky-add-to-cart-btn="enableStickyAddToCartBtn"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.app__product__info {

  .app__product__cta {
    margin: 1rem 0 0.5rem;

    @include productBreakpoint('desktop-s') {
      max-width: 570px;
    }

    &__warning {

      :deep(a) {
        text-decoration: underline;
      }
    }

    .app__product__cta__button {
      position: relative;
      display: inline-block;
      margin-right: 0.5rem;
      width: 100%;

      &:last-of-type {
        margin-bottom: 0;
      }

      @include productBreakpoint('mobile-m') {
        position: relative;
        width: auto;
        text-align: center;
        max-width: none;
        margin-bottom: 0;
      }

      :deep(a) {
        width: 100%;
      }

      &__customize {

        :deep(a) {

          &:hover,
          &:focus {
            color: $color-white;
          }
        }
      }

      &__dealer {

        :deep(a) {

          &:hover,
          &:focus {
            color: $color-white;
          }
        }
      }
    }
  }
}
</style>
